export const READER_QUEUE_KEY = "czasomat.reader.queue.v2";

export type PunchRequest = {
  payload: string;
  pin?: string;
  deviceId: string;
  locationId?: string;
  occurredAt: string;
  idempotencyKey: string;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  photoDataUrl?: string;
};

export type QueuedPunch = PunchRequest & {
  delivery?: { state: "rejected"; status: number; message: string };
};

export type PunchAcknowledgement = {
  ok: true;
  event: { id: string; type?: string; occurredAt?: string };
  employee?: { name?: string; initials?: string };
  duplicate?: boolean;
};

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;
export interface QueueLocks {
  request<T>(name: string, callback: () => T | Promise<T>): Promise<T>;
}

function browserLocks(): QueueLocks | undefined {
  try {
    if (typeof navigator === "undefined" || !navigator.locks) return undefined;
    return { request: async <T>(name: string, callback: () => T | Promise<T>): Promise<T> =>
      await navigator.locks.request(name, async () => await callback()) };
  } catch { return undefined; }
}
export type FlushReport = { acknowledged: Map<string, PunchAcknowledgement>; pending: number; rejected: number; error?: string };

export class QueueStorageError extends Error {}

export function isPermanentHttpFailure(error: unknown): error is Error & { status: number } {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" && status >= 400 && status < 500 && ![408, 425, 429].includes(status);
}

export async function sendReaderPunch(request: PunchRequest): Promise<PunchAcknowledgement> {
  const response = await fetch("/api/v1/kiosk/punch", {
    method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(request)
  });
  const text = await response.text();
  if (!response.ok) {
  let message = "Serwer odrzucił odbicie.";
    try { message = JSON.parse(text).error || message; } catch { /* Preserve HTTP status even for a non-JSON error response. */ }
    throw Object.assign(new Error(message), { status: response.status });
  }
  return JSON.parse(text) as PunchAcknowledgement;
}

export class ReaderQueue {
  private inFlight?: Promise<FlushReport>;

  constructor(private storage: Storage, private send: (request: PunchRequest) => Promise<PunchAcknowledgement>, private locks = browserLocks()) {}

  private locked<T>(scope: "write" | "drain", callback: () => T | Promise<T>): Promise<T> {
    if (!this.locks) return Promise.reject(new QueueStorageError("Ta przeglądarka nie obsługuje bezpiecznej synchronizacji. Odbicie nie zostało zapisane ani wysłane. Użyj obsługiwanej przeglądarki lub skontaktuj się z administratorem."));
    return this.locks.request(`${READER_QUEUE_KEY}:${scope}`, callback);
  }

  read(): QueuedPunch[] {
    try {
      const raw: unknown = JSON.parse(this.storage.getItem(READER_QUEUE_KEY) ?? "[]");
      if (!Array.isArray(raw) || raw.some((item) => !item || typeof item !== "object" ||
        typeof item.idempotencyKey !== "string" || !item.idempotencyKey ||
        typeof item.payload !== "string" || typeof item.deviceId !== "string" || typeof item.occurredAt !== "string")) {
        throw new Error("Invalid queue");
      }
      return raw as QueuedPunch[];
    } catch {
      throw new QueueStorageError("Nie można odczytać zapisanych odbić. Kolejka została zachowana; wymagana pomoc administratora.");
    }
  }

  private write(items: QueuedPunch[]) {
    try {
      this.storage.setItem(READER_QUEUE_KEY, JSON.stringify(items));
    } catch {
      throw new QueueStorageError("Nie można zapisać kolejki odbić (brak miejsca lub dostępu do pamięci). Nie potwierdzono nowego odbicia.");
    }
  }

  enqueue(request: PunchRequest): Promise<void> {
    return this.locked("write", () => {
      const current = this.read();
      if (current.some((item) => item.idempotencyKey === request.idempotencyKey)) {
        throw new QueueStorageError("To odbicie jest już w kolejce. Użyj synchronizacji zapisanej kolejki.");
      }
      this.write([...current, request]);
    });
  }

  flush(): Promise<FlushReport> {
    if (!this.inFlight) this.inFlight = this.locked("drain", () => this.drain()).finally(() => { this.inFlight = undefined; });
    return this.inFlight;
  }

  private async drain(): Promise<FlushReport> {
    const acknowledged = new Map<string, PunchAcknowledgement>();
    let error: string | undefined;
    while (true) {
      const item = await this.locked("write", () => this.read().find((entry) => entry.delivery?.state !== "rejected"));
      if (!item) break;
      const { delivery: _delivery, ...request } = item;
      let response: PunchAcknowledgement;
      try {
        response = await this.send(request);
        if (response?.ok !== true || !response.event?.id) throw new Error("Brak jednoznacznego potwierdzenia serwera.");
      } catch (failure) {
        const message = failure instanceof Error ? failure.message : "Nie udało się potwierdzić odbicia.";
        if (isPermanentHttpFailure(failure)) {
          await this.locked("write", () => this.write(this.read().map((entry) => entry.idempotencyKey === item.idempotencyKey
            ? { ...entry, delivery: { state: "rejected", status: failure.status, message } } : entry)));
          continue;
        }
        error = message;
        break;
      }
      // Re-read after the network await: another punch may have been enqueued meanwhile.
      await this.locked("write", () => this.write(this.read().filter((entry) => entry.idempotencyKey !== item.idempotencyKey)));
      acknowledged.set(item.idempotencyKey, response);
    }
    const remaining = this.read();
    return { acknowledged, pending: remaining.filter((item) => item.delivery?.state !== "rejected").length,
      rejected: remaining.filter((item) => item.delivery?.state === "rejected").length, error };
  }
}
